import type { OrganizationDetail } from 'models';
import { useMemo } from 'react';
import type { HierarchyItem } from 'ui';
import { images } from 'common/utils';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { useGetGroupPreviewIcon, useGetGroupPreviewTypeName } from 'features/groups/hooks';
import { useGetOrganizationIcon, useGetOrganizationTag } from '../../../hooks';

export const useGetHierarchyTreeItems = ({
  getPathToGroupDetail,
  organization,
}: {
  getPathToGroupDetail: (id: string) => string;
  organization: OrganizationDetail;
}): HierarchyItem[] => {
  const getGroupPreviewTypeName = useGetGroupPreviewTypeName();
  const getOrganizationTag = useGetOrganizationTag();
  const getOrganizationIcon = useGetOrganizationIcon();
  const getGroupPreviewIcon = useGetGroupPreviewIcon();

  return useMemo(() => {
    const items: HierarchyItem[] = [];

    if (organization.group) {
      for (const ancestor of organization.group.ancestors) {
        items.push({
          alt: 'Group profile photo',
          avatarUrl: ancestor.logo?.url,
          fallbackPhoto: images.default.groupProfilePhoto,
          id: ancestor.id,
          name: ancestor.name,
          path: getPathToGroupDetail(ancestor.id),
          tag: {
            children: getGroupPreviewTypeName({ groupType: ancestor.groupType }),
            icon: getGroupPreviewIcon({ groupType: ancestor.groupType }),
          },
        });
      }

      const organizationGroup = organization.group;

      items.push(
        {
          alt: 'Group profile photo',
          avatarUrl: organizationGroup.logo?.url,
          fallbackPhoto: images.default.groupProfilePhoto,
          id: organizationGroup.id,
          name: organizationGroup.name,
          path: getPathToGroupDetail(organizationGroup.id),
          tag: {
            children: getGroupPreviewTypeName({ groupType: organizationGroup.groupType }),
            icon: getGroupPreviewIcon({ groupType: organizationGroup.groupType }),
          },
        },
        {
          alt: 'Organization profile photo',
          avatarUrl: organization.profilePhoto?.url,
          fallbackPhoto: images.default.organizationCoverPhoto,
          id: organization.id,
          name: organization.name,
          tag: {
            children: getOrganizationTag({ organization }),
            icon: getOrganizationIcon({ organization }),
          },
        },
      );
    }

    return items;
  }, [
    organization,
    getGroupPreviewTypeName,
    getOrganizationTag,
    getPathToGroupDetail,
    getGroupPreviewIcon,
    getOrganizationIcon,
  ]);
};
